import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { MutationData, unwrapMutationData } from '../shared/graphql-mutation-response.util';
import { ComparatorOperators, ExportRelevanceTreeGQL, ExportRelevanceTreeMutation, GetRelevanceTreeGQL, GetRelevanceTreeQuery, GetRiskMapTreeFiltersGQL, GetRiskMapTreeFiltersQuery, OperatorInput, OrderTypeInput, PagingInput, RiskMapTreeColumns, RiskMapTreeQueryComplexInput, RiskMapTreeSimpleQueryInput, RiskMapTreeSortInput, RiskRelevanceTreeInput, UpdateRelevanceTreeByIdsGQL, UpdateRelevanceTreeByIdsMutation, UpdateTreeNoteRelevanceGQL, UpdateTreeNoteRelevanceMutation } from '../../../graphql/generated';
@Injectable({
    providedIn: 'root'
})
export class RelevanceTreeService {
    private readonly GetRelevanceTreeGQL: GetRelevanceTreeGQL = inject(GetRelevanceTreeGQL);
    private readonly UpdateRelevanceTreeByIdsGQL: UpdateRelevanceTreeByIdsGQL = inject(UpdateRelevanceTreeByIdsGQL);
    private readonly ExportRelevanceTreeGQL: ExportRelevanceTreeGQL = inject(ExportRelevanceTreeGQL);
    private readonly GetRiskMapTreeFiltersGQL: GetRiskMapTreeFiltersGQL = inject(GetRiskMapTreeFiltersGQL);
    private readonly UpdateTreeNoteRelevanceGQL: UpdateTreeNoteRelevanceGQL = inject(UpdateTreeNoteRelevanceGQL);

    getRelevanceTree(legalEntityId: string, campaignId: string, parameters: any, au: boolean | null): Observable<GetRelevanceTreeQuery['relevanceTreeByLegalEntityIdAndCampaignId']> {
        const param = convertLazyLoadToComplexWithoutPage(parameters);
        return this.GetRelevanceTreeGQL.fetch(
            {
                campaignId, legalEntityId, query: param.query as RiskMapTreeQueryComplexInput, sort: param.sort as RiskMapTreeSortInput, au, page: param.page
            }, {
            fetchPolicy: 'no-cache'
        }).pipe(map(result => result.data?.relevanceTreeByLegalEntityIdAndCampaignId));
    }

    getAllRelevanceTree(
        campaignId: string,
        legalEntityId: string,
        au: boolean | null
    ): Observable<GetRiskMapTreeFiltersQuery['riskMapTreeFields']> {
        return this.GetRiskMapTreeFiltersGQL.fetch({ campaignId, legalEntityId, au: null, active: true }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data.riskMapTreeFields))
    }

    updateRelevanceTreeByIds(
        riskRelevanceTree: RiskRelevanceTreeInput[]
    ): Observable<MutationData<UpdateRelevanceTreeByIdsMutation['updateRelevanceTreeByIds']>> {
        return this.UpdateRelevanceTreeByIdsGQL.mutate({ riskRelevanceTree }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data?.updateRelevanceTreeByIds), unwrapMutationData('Unable to update the relevance'))
    }

    exportRelevanceTree(
        campaignId: string,
        legalEntityId: string,
        parameters: any,
        timeZone: string
    ): Observable<ExportRelevanceTreeMutation['downloadRiskRelevanceTree']> {
        const param = convertLazyLoadToComplexWithoutPage(parameters);
        return this.ExportRelevanceTreeGQL.mutate({ campaignId, legalEntityId, timeZone, query: param.query as RiskMapTreeQueryComplexInput, sort: param.sort as RiskMapTreeSortInput }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data?.downloadRiskRelevanceTree))
    }

    updateTreeNote(
        campaignId: string,
        legalEntityId: string,
        processCode: string,
        note: string,
        filter: boolean
    ): Observable<MutationData<UpdateTreeNoteRelevanceMutation['updateTreeNote']>> {
        return this.UpdateTreeNoteRelevanceGQL.mutate({ campaignId, legalEntityId, processCode, note, filter }, { fetchPolicy: 'no-cache' }).pipe(map(res => (res.data?.updateTreeNote)), unwrapMutationData('Unable to update the note'))
    }

}


export function convertLazyLoadToComplexWithoutPage(parameters: any): {
    query: RiskMapTreeQueryComplexInput
    sort: RiskMapTreeSortInput,
    page: PagingInput
} {
    const query: RiskMapTreeSimpleQueryInput[] = [];
    if (parameters) {

        if (parameters.filters) {

            Object.entries(parameters.filters).forEach(([field, values]) => {
                //console.log"value", values);

                if (values !== null && Array.isArray(values) && values.length > 0) {
                    (ENUM_RELEVANCE[field as keyof typeof ENUM_RELEVANCE] || [field]).forEach(key => {
                        query.push({
                            field: key as RiskMapTreeColumns,
                            filters: {
                                operator: (key === RiskMapTreeColumns.ProcessRelevanceValue || key === RiskMapTreeColumns.Level) 
                                ? ComparatorOperators.Eq 
                                : ComparatorOperators.Match,
                                value: key !== RiskMapTreeColumns.Au ? values : values.map(v => v?.toLowerCase() === 'active' || v?.toLowerCase() === 'yes' ? true : false)
                            }
                        });
                    })
                }
            });
        }
    }
    return {
        query: {
            operator: OperatorInput.And,
            query: [
                {
                    query: query,
                    operator: OperatorInput.And
                }
            ]
        },
        sort: {
            sortField: ENUM_RELEVANCE?.[parameters?.order?.sortField as keyof typeof ENUM_RELEVANCE]?.[0] as RiskMapTreeColumns || RiskMapTreeColumns.Sort,
            sortOrder: parameters?.order?.sortDirection === 'ASC' ? OrderTypeInput.Asc : OrderTypeInput.Desc
        },
        page: {
            pageNo: parameters?.page ?? 0,
            pageSize: parameters?.rows ?? 3
        }
    }
}

export const ENUM_RELEVANCE = {
    level: [RiskMapTreeColumns.Level],
    name: [RiskMapTreeColumns.Name],
    code: [RiskMapTreeColumns.Code],
    auditableUniverse: [RiskMapTreeColumns.Au],
    cluster: [RiskMapTreeColumns.Cluster],
    processNote: [RiskMapTreeColumns.Note],
    auditArea: [RiskMapTreeColumns.AuditDomain],
    auditSubArea: [RiskMapTreeColumns.AuditSubdomain],
    processRelevance: [RiskMapTreeColumns.ProcessRelevanceValue]
}